import React, { useEffect, useMemo, useRef, useState } from "react";

import {
	AlumnoLista, ApiTareasPersonal, ArchivoNuevo, DatosTarea, FormaDeEntrega, PortalApiPersonal, ResumenClase, TareaEditable, UnidadPortal
} from "../../../../services/v2/classPortal.service";
import { apiQuizPersonal } from "../../../../services/v2/lmsQuiz.service";
import { aCampoFecha, Icono, mensajeDeError, subirArchivo } from "../comun";
import { AjustesDelQuiz, datosDeForma, formaDeQuiz, PreguntasQuiz, QuizForma, revisarForma, totalDeForma } from "../quiz/editorQuiz";
import { puntosTxt } from "./comunTareas";
import "./tareas.css";

/*
	Nueva Tarea / Editar Tarea (personal, Parte B): título e instrucciones, adjuntos, cómo entregan, rúbrica (suma los puntos),
	para toda la clase o alumnos específicos, unidad, categoría de la nota, vencimiento, tarde con % por día, recordatorio,
	programada y borrador. Solo tareas propias del horario: las del curso o del módulo se cambian desde Contenido.
	Hora de la Ciudad de México. Parte D: la forma "Quiz" abre el editor de preguntas y sus ajustes (los puntos son la suma de
	las preguntas; un quiz ya creado no cambia de forma de entrega).
*/

const FORMAS: [FormaDeEntrega, string, string][] = [["foto", "Foto", "Desde El Celular"], ["archivo", "Archivo", "PDF, Word, Video"], ["quiz", "Quiz", "Se Califica Solo"], ["en_clase", "En Clase", "La Calificas Tú"], ["sin_entrega", "Sin Entrega", "Solo Lectura"]]

interface CriterioForma { id?: number; name: string; points: string }
interface ArchivoEnForma { id?: number; key?: string; link?: string; name: string; mime?: string | null; size?: number }

const Interruptor: React.FC<{ texto: string; valor: boolean; onCambio: (v: boolean) => void; extra?: React.ReactNode }> = ({ texto, valor, onCambio, extra }) => (
	<div className="pcInterruptorFila">
		<span>{texto}</span>
		{extra}
		<button type="button" role="switch" aria-checked={valor} aria-label={texto} className={"pcSw" + (valor ? " pcSwOn" : "")} onClick={() => onCambio(!valor)} />
	</div>
)

interface Props
{
	api: PortalApiPersonal
	apiT: ApiTareasPersonal
	res: ResumenClase
	postId: number | null
	onCerrar: (guardado: string | null) => void
}

const NuevaTarea: React.FC<Props> = ({ api, apiT, res, postId, onCerrar }) => {
	const [cargando, setCargando] = useState<boolean>(postId !== null)
	const [unidades, setUnidades] = useState<UnidadPortal[]>([])
	const [alumnos, setAlumnos] = useState<AlumnoLista[]>([])
	const [inicial, setInicial] = useState<TareaEditable | null>(null)
	const [titulo, setTitulo] = useState<string>("")
	const [cuerpo, setCuerpo] = useState<string>("")
	const [forma, setForma] = useState<FormaDeEntrega>("foto")
	const [rubrica, setRubrica] = useState<CriterioForma[]>([])
	const [puntos, setPuntos] = useState<string>("20")
	const [categoria, setCategoria] = useState<string>("")
	const [unidad, setUnidad] = useState<string>("")
	const [para, setPara] = useState<"clase" | "lista">("clase")
	const [elegidos, setElegidos] = useState<number[]>([])
	const [venceFecha, setVenceFecha] = useState<string>("")
	const [venceHora, setVenceHora] = useState<string>("23:59")
	const [aceptaTarde, setAceptaTarde] = useState<boolean>(true)
	const [pctDia, setPctDia] = useState<string>("10")
	const [recordar, setRecordar] = useState<boolean>(true)
	const [programar, setProgramar] = useState<boolean>(false)
	const [programada, setProgramada] = useState<string>("")
	const [archivos, setArchivos] = useState<ArchivoEnForma[]>([])
	const [enlace, setEnlace] = useState<string>("")
	const [subiendo, setSubiendo] = useState<{ nombre: string; porciento: number } | null>(null)
	const [guardando, setGuardando] = useState<boolean>(false)
	const [error, setError] = useState<string | null>(null)
	const [quiz, setQuiz] = useState<QuizForma>(() => formaDeQuiz(null))
	const [quizConIntentos, setQuizConIntentos] = useState<boolean>(false)
	const [subiendoImg, setSubiendoImg] = useState<number>(0)
	const firmaInicial = useRef<string | null>(null)
	const entrada = useRef<HTMLInputElement>(null)

	const categorias = useMemo(() => (res.scheme?.categories || []).filter((c) => !c.esAsistencia), [res.scheme])

	useEffect(() => {
		api.units().then(setUnidades).catch(() => setUnidades([]))
		api.students().then(setAlumnos).catch(() => setAlumnos([]))
	}, [api])

	useEffect(() => {
		if (postId === null) return
		apiT.get(postId).then((t) => {
			setInicial(t); setTitulo(t.title); setCuerpo(t.body || ""); setForma(t.delivery); setRubrica(t.rubric.map((c) => ({ id: c.id, name: c.name, points: String(c.points) })))
			setPuntos(String(t.points)); setCategoria(t.category || ""); setUnidad(t.unitId ? String(t.unitId) : "")
			setPara(t.audience); setElegidos(t.studentIds)
			if (t.dueAt) { setVenceFecha(t.dueAt.slice(0, 10)); setVenceHora(t.dueAt.slice(11, 16)) }
			setAceptaTarde(t.acceptLate); setPctDia(String(t.latePenaltyPct)); setRecordar(t.remindDayBefore)
			if (t.publishAt) { setProgramar(true); setProgramada(t.publishAt) }
			setArchivos(t.files.map((f) => ({ id: f.id, name: f.name, mime: f.mime, size: f.size, link: f.kind === "enlace" ? f.url || undefined : undefined })))
			if (t.delivery === "quiz") {
				const poner = (q: NonNullable<TareaEditable["quiz"]>) => { setQuiz(formaDeQuiz(q)); setQuizConIntentos(q.hasAttempts) }
				if (t.quiz) poner(t.quiz)
				else return apiQuizPersonal(res.scheduleId).get(t.id).then(poner)
			}
		}).catch((e) => setError(mensajeDeError(e, "No pudimos abrir la tarea."))).finally(() => setCargando(false))
	}, [apiT, postId, res.scheduleId])

	const esQuiz = forma === "quiz"
	const califica = forma !== "sin_entrega"
	const conRubrica = califica && !esQuiz && rubrica.length > 0
	const totalRubrica = rubrica.reduce((a, c) => a + (Number(c.points) || 0), 0)
	const bloqueaRubrica = !!inicial?.hasGrades
	const bloqueaForma = !!inicial && (inicial.hasSubmissions || inicial.hasGrades)

	// Lo que hay en la forma, para avisar antes de cerrar con cambios sin guardar.
	const firma = JSON.stringify({ titulo, cuerpo, forma, rubrica, puntos, categoria, unidad, para, elegidos, venceFecha, venceHora, aceptaTarde, pctDia, recordar, programar, programada,
		archivos: archivos.map((a) => a.id || a.key || a.link), quiz: forma === "quiz" ? datosDeForma(quiz) : null })
	useEffect(() => { if (!cargando && firmaInicial.current === null) firmaInicial.current = firma }, [cargando, firma])

	const agregarArchivos = async (lista: FileList | null) => {
		if (!lista) return
		setError(null)
		for (const f of Array.from(lista)) {
			if (archivos.length >= 10) { setError("Máximo 10 archivos."); break }
			if (f.size > 200 * 1024 * 1024) { setError("\"" + f.name + "\" pesa más de 200 MB."); continue }
			try {
				setSubiendo({ nombre: f.name, porciento: 0 })
				const key = await subirArchivo(api, f, "adjunto", (p) => setSubiendo({ nombre: f.name, porciento: p }))
				setArchivos((a) => [...a, { key, name: f.name, mime: f.type || null, size: f.size }])
			} catch (e) { setError(mensajeDeError(e, "No se pudo subir \"" + f.name + "\".")) }
		}
		setSubiendo(null)
		if (entrada.current) entrada.current.value = ""
	}
	const agregarEnlace = () => {
		const u = enlace.trim()
		if (!/^https?:\/\/\S+$/i.test(u)) { setError("El enlace debe empezar con http:// o https://."); return }
		let nombre = u
		try { nombre = new URL(u).hostname.replace(/^www\./, "") } catch { /* se queda la dirección */ }
		setArchivos((a) => [...a, { link: u, name: nombre }]); setEnlace(""); setError(null)
	}
	const cambiarCriterio = (i: number, c: Partial<CriterioForma>) => setRubrica((r) => r.map((x, j) => (j === i ? { ...x, ...c } : x)))

	const guardar = async (borrador: boolean) => {
		if (guardando || subiendo || subiendoImg > 0) return
		setError(null)
		if (titulo.trim() === "") { setError("Ponle un título a la tarea."); return }
		if (califica && !categoria) { setError("Elige en qué categoría de la nota cuenta la tarea."); return }
		if (califica && !esQuiz && !conRubrica && !(Number(puntos) > 0)) { setError(puntos.trim() === "" ? "Escribe los puntos de la tarea." : "Los puntos deben ser mayores que 0."); return }
		if (esQuiz) {
			const problemaQuiz = revisarForma(quiz)
			if (problemaQuiz) { setError(problemaQuiz); return }
			if (!borrador && quiz.preguntas.length === 0) { setError("Agrega al menos una pregunta, o guarda el quiz como borrador."); return }
			if (quizConIntentos && !window.confirm("Ya hay alumnos que entregaron este quiz. Al guardar se vuelven a calificar todos contra estas preguntas. ¿Guardar?")) return
		}
		if (para === "lista" && elegidos.length === 0) { setError("Elige al menos un alumno."); return }
		if (programar && !programada) { setError("Elige cuándo se publica."); return }
		//A due date already in the past (CDMX wall time, like the field) counts everyone as late at once: ask, like the poll does (QA M7).
		const vence = venceFecha ? venceFecha + "T" + (venceHora || "23:59") : ""
		const venceAntes = inicial?.dueAt ? inicial.dueAt.slice(0, 16).replace(" ", "T") : ""
		if (!borrador && vence !== "" && vence !== venceAntes && vence < aCampoFecha(Date.now())
			&& !window.confirm("La fecha de entrega ya pasó: quien no haya entregado saldrá de inmediato como No Entregó. ¿Guardar así?")) return
		setGuardando(true)
		const files: ArchivoNuevo[] = archivos.map((a) => a.id ? { id: a.id, name: a.name } : a.link ? { link: a.link, name: a.name } : { key: a.key, name: a.name, mime: a.mime, size: a.size })
		const d: DatosTarea = {
			title: titulo.trim(), body: cuerpo.trim(), delivery: forma, category: califica ? categoria : null, unitId: unidad ? Number(unidad) : null,
			points: califica && !esQuiz && !conRubrica ? Number(puntos) : null, rubric: califica && !esQuiz ? rubrica.map((c) => ({ id: c.id, name: c.name.trim(), points: Number(c.points) || 0 })) : [],
			dueAt: venceFecha ? venceFecha + "T" + (venceHora || "23:59") : null, acceptLate: aceptaTarde, latePenaltyPct: aceptaTarde ? Number(pctDia) || 0 : 0,
			remindDayBefore: recordar, audience: para, studentIds: para === "lista" ? elegidos : [], publishAt: programar ? programada : null, draft: borrador, files,
			...(esQuiz ? { quiz: datosDeForma(quiz) } : {}),
		}
		try {
			const t = postId === null ? await apiT.create(d) : await apiT.update(postId, d)
			const que = esQuiz ? "Quiz" : "Tarea"
			const o = esQuiz ? "o" : "a"
			onCerrar(t.status === "borrador" ? "Guardado Como Borrador" : t.status === "programada" ? que + " Programad" + o : postId === null ? que + " Publicad" + o : que + " Guardad" + o)
		} catch (e) { setError(mensajeDeError(e)); setGuardando(false) }
	}

	const borrar = async () => {
		if (postId === null || guardando) return
		const aviso = inicial && (inicial.hasSubmissions || inicial.hasGrades) ? " Ya tiene entregas o notas: se conservan guardadas, pero la tarea deja de verse." : ""
		if (!window.confirm("¿Borrar esta tarea?" + aviso)) return
		setGuardando(true); setError(null)
		try { await api.deletePost(postId); onCerrar("Tarea Borrada") } catch (e) { setError(mensajeDeError(e)); setGuardando(false) }
	}

	if (cargando) return <div className="pc"><div className="pcCargando">Cargando…</div></div>
	const ocupadoSubiendo = !!subiendo || subiendoImg > 0
	const cerrar = () => {
		if (firmaInicial.current !== null && firma !== firmaInicial.current && !window.confirm("¿Salir sin guardar? Se pierden los cambios.")) return
		onCerrar(null)
	}
	const editando = postId !== null

	return (
		<div className="pc">
			<header className="pcCabeza">
				<button type="button" className="pcIb" aria-label="Cerrar" onClick={cerrar}><Icono n="cerrar" size={22} /></button>
				<div className="pcCabezaTitulo"><div className="pcT">{editando ? (esQuiz ? "Editar Quiz" : "Editar Tarea") : "Nueva Tarea"}</div><div className="pcTenue pcChico">{[res.title, res.period].filter(Boolean).join(" · ")}</div></div>
				<button type="button" className="pcBtn pcBtnS" disabled={guardando || ocupadoSubiendo} onClick={() => guardar(true)}>Guardar Borrador</button>
				<button type="button" className="pcBtn pcBtnP" disabled={guardando || ocupadoSubiendo} title={ocupadoSubiendo ? "Espera a que termine de subir" : undefined} onClick={() => guardar(false)}>{programar ? "Programar" : editando && inicial?.status !== "borrador" ? "Guardar" : "Publicar"}</button>
			</header>
			<main className="tkDosColumnas">
				<div className="tkColPrincipal">
					{error ? <div className="pcAviso tkAvisoOro" role="alert">{error}</div> : null}
					<div className="pcTarjeta" style={{ padding: 20, gap: 16 }}>
						<div className="pcCampo"><label className="pcCampoEtiqueta" htmlFor="ntTitulo">Título</label>
							<input id="ntTitulo" className="pcInput" style={{ height: 52, fontWeight: 600, fontSize: 17 }} value={titulo} maxLength={200} onChange={(e) => setTitulo(e.target.value)} /></div>
						<div className="pcCampo"><label className="pcCampoEtiqueta" htmlFor="ntCuerpo">Instrucciones</label>
							<textarea id="ntCuerpo" className="pcTextarea" rows={5} value={cuerpo} maxLength={5000} onChange={(e) => setCuerpo(e.target.value)} /></div>
						<div className="pcCampo">
							<span className="pcCampoEtiqueta">Adjuntos</span>
							{archivos.length > 0 ? (
								<div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
									{archivos.map((a, i) => (
										<div className="pcAdjuntoFila" key={(a.id || a.key || a.link) + "-" + i}>
											<Icono n={a.link ? "enlace" : "doc"} /><span className="pcNombre">{a.name}</span>
											<button type="button" className="pcIb" style={{ width: 36, height: 36 }} aria-label={"Quitar " + a.name} onClick={() => setArchivos(archivos.filter((_, j) => j !== i))}><Icono n="cerrar" size={16} /></button>
										</div>
									))}
								</div>
							) : null}
							{subiendo ? <div><div className="pcChico pcTenue">Subiendo {subiendo.nombre} {subiendo.porciento}%</div><div className="pcBarra"><div style={{ width: subiendo.porciento + "%" }} /></div></div> : null}
							<div className="pcRedactorBotones">
								<input ref={entrada} type="file" multiple hidden onChange={(e) => agregarArchivos(e.target.files)} />
								<button type="button" className="pcBtn pcBtnS" disabled={!!subiendo} onClick={() => entrada.current?.click()}><Icono n="clip" />Subir Archivo</button>
								<input className="pcInput" style={{ flex: "1 1 200px", height: 44 }} aria-label="Enlace" placeholder="Pegar Enlace O Video" value={enlace} onChange={(e) => setEnlace(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); agregarEnlace() } }} />
								<button type="button" className="pcBtn pcBtnS" disabled={enlace.trim() === ""} onClick={agregarEnlace}><Icono n="enlace" />Agregar Enlace</button>
							</div>
						</div>
					</div>

					<div className="pcTarjeta" style={{ padding: 20 }}>
						<span className="pcCampoEtiqueta">Cómo Entregan</span>
						<div className="tkSegmentos" role="group" aria-label="Cómo entregan">
							{FORMAS.map(([id, texto, sub]) => (
								<button key={id} type="button" className="tkSegmento" aria-pressed={forma === id} disabled={(bloqueaForma && id !== forma) || (!!inicial && (id === "quiz") !== (inicial.delivery === "quiz"))} onClick={() => setForma(id)}>{texto}<small>{sub}</small></button>
							))}
						</div>
						{bloqueaForma ? <div className="pcAyuda pcTenue pcChico">Ya hay entregas o notas: no se puede cambiar cómo entregan.</div>
							: inicial ? <div className="pcAyuda pcTenue pcChico">{inicial.delivery === "quiz" ? "Un quiz no cambia de forma de entrega." : "Una tarea ya creada no se vuelve quiz: crea un quiz nuevo."}</div> : null}
					</div>

					{esQuiz ? (
						<>
							{quizConIntentos ? <div className="tkAvisoOro">Ya hay alumnos que entregaron: si cambias preguntas o puntos, al guardar se vuelven a calificar todos.</div> : null}
							<PreguntasQuiz forma={quiz} onCambio={setQuiz} subirImagen={(f) => subirArchivo(api, f, "adjunto")} onSubiendo={(v) => setSubiendoImg((n) => Math.max(0, n + (v ? 1 : -1)))} />
						</>
					) : null}

					{califica && !esQuiz ? (
						<div className="pcTarjeta" style={{ padding: 20 }}>
							<div className="pcFilaAutor"><span className="pcCampoEtiqueta" style={{ flex: 1 }}>Rúbrica</span>{conRubrica ? <span className="pcTenue">Total <b style={{ color: "var(--pc-tinta)" }}>{puntosTxt(totalRubrica)} Pts</b></span> : null}</div>
							{rubrica.length === 0 ? <div className="pcTenue pcChico">Sin rúbrica: la calificas con una sola nota de hasta los puntos de la tarea. Agrega criterios y la nota se arma sumándolos.</div> : null}
							{rubrica.map((c, i) => (
								<div className="tkCriterio" key={c.id ?? "n" + i}>
									<label style={{ position: "absolute", left: -9999 }} htmlFor={"crN" + i}>Criterio {i + 1}</label>
									<input id={"crN" + i} className="pcInput" placeholder="Nombre Del Criterio" maxLength={160} value={c.name} onChange={(e) => cambiarCriterio(i, { name: e.target.value })} />
									<label style={{ position: "absolute", left: -9999 }} htmlFor={"crP" + i}>Puntos del criterio {i + 1}</label>
									<input id={"crP" + i} className="tkPts" inputMode="decimal" value={c.points} disabled={bloqueaRubrica} onChange={(e) => cambiarCriterio(i, { points: e.target.value })} />
									<button type="button" className="pcIb" style={{ width: 40, height: 40 }} aria-label={"Quitar criterio " + (i + 1)} disabled={bloqueaRubrica} onClick={() => setRubrica(rubrica.filter((_, j) => j !== i))}><Icono n="cerrar" size={16} /></button>
								</div>
							))}
							{bloqueaRubrica ? <div className="pcTenue pcChico">Ya hay notas con esta rúbrica: solo puedes cambiar los nombres de los criterios.</div> : null}
							{!bloqueaRubrica && rubrica.length < 12 ? <button type="button" className="pcBtn pcBtnS" style={{ alignSelf: "flex-start" }} onClick={() => setRubrica([...rubrica, { name: "", points: "5" }])}><Icono n="mas" size={16} />Agregar Criterio</button> : null}
						</div>
					) : null}
				</div>

				<aside className="tkColLateral">
					<div className="pcTarjeta" style={{ padding: 20, gap: 14 }}>
						<div className="pcCampo"><label className="pcCampoEtiqueta" htmlFor="ntPara">Para</label>
							<select id="ntPara" className="pcSelect" value={para} onChange={(e) => setPara(e.target.value as "clase" | "lista")}>
								<option value="clase">Toda La Clase · {res.students} {res.students === 1 ? "Alumno" : "Alumnos"}</option>
								<option value="lista">Alumnos Específicos</option>
							</select></div>
						{para === "lista" ? (
							<div className="tkAlumnosLista" role="group" aria-label="Alumnos de la tarea">
								{alumnos.map((a) => (
									<label key={a.id}><input type="checkbox" checked={a.id !== undefined && elegidos.includes(a.id)} onChange={(e) => a.id !== undefined && setElegidos(e.target.checked ? [...elegidos, a.id] : elegidos.filter((x) => x !== a.id))} />{a.name}</label>
								))}
							</div>
						) : null}
						{unidades.length > 0 ? (
							<div className="pcCampo"><label className="pcCampoEtiqueta" htmlFor="ntUnidad">Unidad</label>
								<select id="ntUnidad" className="pcSelect" value={unidad} onChange={(e) => setUnidad(e.target.value)}>
									<option value="">Sin Unidad</option>
									{unidades.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
								</select></div>
						) : null}
						{califica ? (
							<div className="tkFilaCampos">
								<div className="pcCampo"><label className="pcCampoEtiqueta" htmlFor="ntPuntos">Puntos</label>
									<input id="ntPuntos" className="pcInput" inputMode="decimal" value={esQuiz ? puntosTxt(totalDeForma(quiz)) : conRubrica ? puntosTxt(totalRubrica) : puntos} disabled={conRubrica || esQuiz} title={esQuiz ? "Suma de las preguntas" : undefined} onChange={(e) => setPuntos(e.target.value)} /></div>
								<div className="pcCampo" style={{ flex: 1.5 }}><label className="pcCampoEtiqueta" htmlFor="ntCat">Cuenta Para</label>
									<select id="ntCat" className="pcSelect" value={categoria} onChange={(e) => setCategoria(e.target.value)}>
										<option value="">Elige…</option>
										{categorias.map((c) => <option key={c.name} value={c.name}>{c.name} · {puntosTxt(c.weight)}%</option>)}
									</select></div>
							</div>
						) : null}
						<div className="tkFilaCampos">
							<div className="pcCampo"><label className="pcCampoEtiqueta" htmlFor="ntVence">{forma === "en_clase" ? "Fecha" : "Vence"}</label>
								<input id="ntVence" type="date" className="pcInput" value={venceFecha} onChange={(e) => setVenceFecha(e.target.value)} /></div>
							<div className="pcCampo"><label className="pcCampoEtiqueta" htmlFor="ntHora">Hora</label>
								<input id="ntHora" type="time" className="pcInput" value={venceHora} onChange={(e) => setVenceHora(e.target.value)} /></div>
						</div>
						<div className="pcTenue pcChico">Hora de la Ciudad de México.</div>
					</div>

					{esQuiz ? <AjustesDelQuiz forma={quiz} onCambio={setQuiz} /> : null}

					{forma === "foto" || forma === "archivo" || esQuiz ? (
						<div className="pcTarjeta" style={{ padding: "6px 20px", gap: 0 }}>
							<Interruptor texto="Aceptar Entregas Tarde" valor={aceptaTarde} onCambio={setAceptaTarde} />
							{aceptaTarde ? (
								<div className="pcInterruptorFila"><span>Restar Por Día Tarde</span>
									<span className="tkPorcentaje"><label style={{ position: "absolute", left: -9999 }} htmlFor="ntPct">Porcentaje por día tarde</label>
										<input id="ntPct" inputMode="decimal" value={pctDia} onChange={(e) => setPctDia(e.target.value)} /><b>%</b></span></div>
							) : null}
							<Interruptor texto="Recordar 1 Día Antes" valor={recordar} onCambio={setRecordar} />
							<Interruptor texto="Programar Publicación" valor={programar} onCambio={(v) => { setProgramar(v); if (!v) setProgramada("") }} />
							{programar ? (
								<div className="pcCampo" style={{ padding: "0 0 14px" }}><label className="pcCampoEtiqueta" htmlFor="ntProg">Se Publica</label>
									<input id="ntProg" type="datetime-local" className="pcInput" value={programada} onChange={(e) => setProgramada(e.target.value)} /></div>
							) : null}
						</div>
					) : (
						<div className="pcTarjeta" style={{ padding: "6px 20px", gap: 0 }}>
							<Interruptor texto="Programar Publicación" valor={programar} onCambio={(v) => { setProgramar(v); if (!v) setProgramada("") }} />
							{programar ? (
								<div className="pcCampo" style={{ padding: "0 0 14px" }}><label className="pcCampoEtiqueta" htmlFor="ntProg2">Se Publica</label>
									<input id="ntProg2" type="datetime-local" className="pcInput" value={programada} onChange={(e) => setProgramada(e.target.value)} /></div>
							) : null}
						</div>
					)}

					{editando ? (
						<button type="button" className="pcBtn pcBtnS" disabled={guardando} onClick={borrar}>Borrar Tarea</button>
					) : null}

					<div className="pcTarjeta" style={{ padding: "14px 18px", background: "var(--pc-acento-lavado)", fontSize: 14, lineHeight: 1.4 }}>
						Los alumnos la ven en Mi Clase cuando se publica. Si es de entrega, te avisamos aquí cuando entreguen.
					</div>
				</aside>
			</main>
		</div>
	)
}

export default NuevaTarea
